/**
 * Shared builder for the analysis-result toast.
 *
 * Two independent code paths surface a background/queued analysis verdict as a toast:
 * the queued path (analysisQueue relaunch → App.handleQueuedVerdict) and the
 * analysis-phase-background path (CameraScreen.notifyAnalysisResult). Both must produce
 * IDENTICAL wording, so the text is built here once, from the raw /item-comparison
 * response, and reused by both.
 *
 * Detail (Eric): the toast names WHAT is missing, grouped by product name:
 *   1–2 groups → list them all ("faltan 2 salsas y 3 hamburguesas")
 *   >2 groups  → first group + the rest collapsed ("faltan 2 salsas y otros 7 productos")
 */

/** Normalize the /item-comparison `missing_items` map into a flat [{groupName, missingQuantity}]. */
export function extractMissingItems(rawMissing) {
  if (!rawMissing || typeof rawMissing !== 'object') return [];
  return Object.values(rawMissing).map((v) => ({
    groupName: v.group_name,
    missingQuantity: v.missing_quantity || 0,
  }));
}

/** Uppercase the first character. The body line stands alone in the toast card (it used to be
 *  embedded after "Pedido {id}: "), so it must read as a sentence: "Faltan…" not "faltan…". */
function capitalizeFirst(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** Collapse missing items to one entry per product name, summing quantities; drops zero/negatives. */
function groupMissing(missingItems) {
  const map = new Map();
  for (const it of missingItems || []) {
    const qty = it.missingQuantity || 0;
    if (qty <= 0) continue;
    const name = it.groupName || '';
    map.set(name, (map.get(name) || 0) + qty);
  }
  return [...map.entries()].map(([name, qty]) => ({ name, qty }));
}

/** Split `text` on each chunk in `dataChunks` (matched in order, left to right), returning
 *  [{text, data}] where `data: true` marks a chunk and `data: false` marks what sits between
 *  them. Used to carve the POS item names (data) out of the otherwise-translatable sentence
 *  without re-deriving the sentence text — the caller passes the EXACT string it already built,
 *  so the plain-string form and the segmented form can never drift apart. */
function splitDataChunks(text, dataChunks) {
  const segments = [];
  let cursor = 0;
  for (const chunk of dataChunks) {
    if (!chunk) continue;
    const idx = text.indexOf(chunk, cursor);
    if (idx === -1) continue; // defensive: chunk not found (shouldn't happen) — leave it as plain text below
    if (idx > cursor) segments.push({ text: text.slice(cursor, idx), data: false });
    segments.push({ text: chunk, data: true });
    cursor = idx + chunk.length;
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), data: false });
  return segments;
}

/**
 * Build the { tag, body, bodySegments, variant } for the result toast. The order id is NOT
 * embedded here — the toast renders it separately, in the rider-toast card layout (a tag chip +
 * the big order id + this body line), so it reads as the same toast Adrián designed for riders.
 *
 * `body` is the plain sentence (byte-identical to the pre-notranslate wording) for any consumer
 * that only needs text. `bodySegments` is the same sentence split into [{text, data}], `data:
 * true` marking exactly the POS product names (Chrome's page translation rewrote these on a
 * Portuguese tablet — "Esparregado"→"Aspargo" — the connecting words ("faltan", "y") stay
 * translatable so Chrome still serves clients whose client.language has no dictionary).
 * @param {{isCorrect:boolean, missingItems:Array<{groupName:string, missingQuantity:number}>}} result
 * @param {(key:string, ...rest:any[])=>string} t  a bound vue-i18n $t
 */
export function analysisResultToast({ isCorrect, missingItems }, t) {
  const groups = groupMissing(missingItems);
  if (isCorrect === true || groups.length === 0) {
    return { tag: t('message.tagCorrect'), body: '', bodySegments: [], variant: 'success' };
  }

  const total = groups.reduce((sum, g) => sum + g.qty, 0);
  const verb = t('message.missingVerb', total, { count: total });
  const and = t('message.and');

  let rest;
  let dataChunks;
  if (groups.length <= 2) {
    rest = groups.map((g) => `${g.qty} ${g.name}`).join(` ${and} `);
    dataChunks = groups.map((g) => `${g.qty} ${g.name}`);
  } else {
    const first = groups[0];
    const restCount = total - first.qty;
    rest = `${first.qty} ${first.name} ${and} ${t('message.missingOthers', restCount, { count: restCount })}`;
    dataChunks = [`${first.qty} ${first.name}`];
  }

  const body = capitalizeFirst(`${verb} ${rest}`);
  const bodySegments = splitDataChunks(body, dataChunks);

  return { tag: t('message.tagReview'), body, bodySegments, variant: 'warning' };
}

/**
 * Lo que se registra cuando la cocina decide qué hacer con un veredicto que dice que falta algo.
 *
 * Vive aquí, y no dentro del componente, porque es el dato con el que se decide si el piloto de IA
 * wrong se queda o se tira: tiene que poder probarse sin montar media pantalla. El carril forense
 * no es observable desde los e2e (Amplify no resuelve credenciales contra el Cognito mockeado y el
 * POST nunca sale), así que esta función es la única cobertura real del contrato.
 *
 * Devuelve `null` cuando no había productos marcados como que faltaban: lo que se mide es el aviso,
 * no el cierre de un veredicto correcto.
 *
 * @param {object} args
 * @param {string} args.decision        'retake' | 'deliver' | 'background' | 'dismiss'
 * @param {object|null} args.order      el pedido abierto
 * @param {Array} args.missingItems     analysisResult.missingItems
 * @param {number|null} args.photos     fotos hechas hasta ahora
 * @param {number|null} args.shownAt    cuándo se pintó el veredicto (ms)
 * @param {number} args.now             reloj (inyectado para poder testarlo)
 * @param {boolean} args.reasonsEnabled brazo del experimento tal y como se PINTÓ en esta tablet
 * @param {string|null} args.reason     motivo elegido, si lo hubo
 * @param {number|null} args.reasonMs   lo que se tardó en contestar la pregunta, aparte
 */
export function verdictDecisionPayload({
  decision, order = null, missingItems = [], photos = null,
  shownAt = null, now = Date.now(), reasonsEnabled = false, reason = null, reasonMs = null,
}) {
  if (!Array.isArray(missingItems) || missingItems.length === 0) return null;
  return {
    decision,
    order_id: order?.orderId ?? null,
    // La misma llave que `analysis_verdict` y `ai_wrong_reason`: sin ella, cruzar la decisión con
    // su veredicto es un apaño por tiempo en vez de un join.
    order_analysis_id: order?.orderAnalysis || null,
    missing_count: missingItems.reduce((total, item) => total + (Number(item.missingQuantity) || 0), 0),
    missing_groups: missingItems.length,
    photos,
    // El coste de preguntar se paga en segundos con un rider esperando: este es el guardarraíl.
    shown_ms: Number.isFinite(shownAt) ? Math.max(0, now - shownAt) : null,
    reasons_enabled: reasonsEnabled === true,
    reason: reason || null,
    // Aparte de `shown_ms`, que incluye la pregunta por construcción: sin separarlos, el
    // guardarraíl del coste salta por el propio diseño y no dice si el segundo paso cuesta o si la
    // gente se lo piensa más delante del primero.
    reason_ms: Number.isFinite(reasonMs) ? Math.max(0, reasonMs) : null,
  };
}
